import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, DollarSign, FileText, Image, Calendar, BarChart3 } from 'lucide-react';

const CostDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/costs/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading cost dashboard:', error);
      toast.error('Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 md:p-8 flex items-center justify-center">
        <div className="text-sky-400 animate-pulse">Chargement des statistiques...</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen p-4 md:p-8 flex items-center justify-center">
        <div className="text-slate-400">Aucune donnée disponible</div>
      </div>
    );
  }

  // Calcul du max pour les barres
  const maxMonthly = Math.max(...dashboard.monthly_history.map(m => m.total_eur), 0.01);

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="cost-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold font-orbitron tracking-widest uppercase text-sky-400 text-glow mb-2">
            Tableau de bord des coûts
          </h1>
          <p className="text-slate-400">Suivez vos dépenses IA en temps réel</p>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Ce mois */}
          <Card className="bg-gradient-to-br from-sky-500/10 to-sky-500/5 border-sky-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-sky-300 uppercase tracking-wider mb-1">Ce mois</p>
                  <p className="text-3xl font-bold text-sky-400">{dashboard.total_this_month_eur.toFixed(2)} €</p>
                  <p className="text-sm text-slate-400 mt-1">{dashboard.articles_this_month} article(s)</p>
                </div>
                <div className="w-14 h-14 rounded-full bg-sky-500/20 flex items-center justify-center">
                  <Calendar className="w-7 h-7 text-sky-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mois dernier */}
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-300 uppercase tracking-wider mb-1">Mois dernier</p>
                  <p className="text-3xl font-bold text-purple-400">{dashboard.total_last_month_eur.toFixed(2)} €</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {dashboard.total_this_month_eur > dashboard.total_last_month_eur ? (
                      <span className="text-amber-400">↑ En hausse</span>
                    ) : dashboard.total_this_month_eur < dashboard.total_last_month_eur ? (
                      <span className="text-green-400">↓ En baisse</span>
                    ) : (
                      <span className="text-slate-400">= Stable</span>
                    )}
                  </p>
                </div>
                <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total */}
          <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-300 uppercase tracking-wider mb-1">Total cumulé</p>
                  <p className="text-3xl font-bold text-green-400">{dashboard.total_all_time_eur.toFixed(2)} €</p>
                  <p className="text-sm text-slate-400 mt-1">Depuis le début</p>
                </div>
                <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-7 h-7 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Breakdown & Sites */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Répartition par type */}
          <Card className="bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
            <CardHeader>
              <CardTitle className="text-lg font-orbitron uppercase text-sky-400 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Répartition par type
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-300">Génération de texte</p>
                    <p className="text-xs text-slate-500">Claude Sonnet 4.5</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-blue-400">{dashboard.breakdown.text_generation_eur.toFixed(2)} €</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                    <Image className="w-5 h-5 text-pink-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-300">Génération d'images</p>
                    <p className="text-xs text-slate-500">Gemini Nano Banana</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-pink-400">{dashboard.breakdown.image_generation_eur.toFixed(2)} €</p>
              </div>
              
              {/* Progress bars */}
              <div className="pt-4 space-y-3">
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Texte</span>
                    <span>{dashboard.total_all_time_eur > 0 ? Math.round(dashboard.breakdown.text_generation_eur / dashboard.total_all_time_eur * 100) : 0}%</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_all_time_eur > 0 ? (dashboard.breakdown.text_generation_eur / dashboard.total_all_time_eur * 100) : 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Images</span>
                    <span>{dashboard.total_all_time_eur > 0 ? Math.round(dashboard.breakdown.image_generation_eur / dashboard.total_all_time_eur * 100) : 0}%</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-pink-500 to-pink-400 rounded-full transition-all duration-500"
                      style={{ width: `${dashboard.total_all_time_eur > 0 ? (dashboard.breakdown.image_generation_eur / dashboard.total_all_time_eur * 100) : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Par site (ce mois) */}
          <Card className="bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
            <CardHeader>
              <CardTitle className="text-lg font-orbitron uppercase text-sky-400">
                Coûts par site (ce mois)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(dashboard.by_site).length === 0 ? (
                <p className="text-slate-500 text-center py-8">Aucune génération ce mois</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(dashboard.by_site)
                    .sort((a, b) => b[1] - a[1])
                    .map(([site, cost]) => (
                      <div key={site} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-800">
                        <span className="text-slate-300 font-medium">{site}</span>
                        <span className="text-sky-400 font-bold">{cost.toFixed(2)} €</span>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Historique mensuel */}
        <Card className="bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
          <CardHeader>
            <CardTitle className="text-lg font-orbitron uppercase text-sky-400">
              Historique des 6 derniers mois
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between gap-2 h-48">
              {dashboard.monthly_history.slice().reverse().map((month, index) => (
                <div key={month.month} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-gradient-to-t from-sky-500 to-sky-400 rounded-t-lg transition-all duration-500 hover:from-sky-400 hover:to-sky-300"
                    style={{ 
                      height: `${Math.max((month.total_eur / maxMonthly) * 100, 5)}%`,
                      minHeight: '8px'
                    }}
                  />
                  <div className="mt-2 text-center">
                    <p className="text-xs text-slate-500">{month.month.split('-')[1]}/{month.month.split('-')[0].slice(2)}</p>
                    <p className="text-sm font-bold text-slate-300">{month.total_eur.toFixed(2)}€</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-600">
            Les coûts sont des estimations basées sur les tarifs standard des API (Claude ~0.03€/article, Gemini ~0.02€/image)
          </p>
        </div>
      </div>
    </div>
  );
};

export default CostDashboard;
